import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import Attachments from '@/Pages/Run/components/Attachments';
import { getAttachments } from '@/Pages/Run/utils/multimodalRunUtils';
import { CodeLanguageType } from '@/components/Code/types';
import { ExampleSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { filterInputs } from '../../RunsTable/utils/filterInputs';
import { CompactModeCell, FullModeCell } from '../utils/cellComponents';

const columnHelper = createColumnHelper<ExampleSchema>();

const INPUT_TRUNCATE_LIMIT = 1000;
const OUTPUT_TRUNCATE_LIMIT = 1000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const COLUMNS_DEF: ColumnDef<ExampleSchema, any>[] = [
  columnHelper.accessor(
    (row) => {
      // Access displayMode from table meta at render time
      return row.inputs;
    },
    {
      id: 'inputs',
      header: 'Inputs',
      enableResizing: true,
      enableSorting: false,
      cell: ({ getValue, table }) => {
        const { displayMode = 'compact', language = 'json' } = (table.options
          .meta ?? {}) as {
          displayMode?: 'compact' | 'full';
          language?: CodeLanguageType;
        };

        const rawValue = getValue();
        const value =
          displayMode === 'full'
            ? rawValue
            : filterInputs(rawValue, { removeKeys: ['functions'] })?.length
            ? filterInputs(rawValue, { removeKeys: ['functions'] })
            : null;

        if (!value) return <i className="text-tertiary">No inputs</i>;

        if (displayMode === 'full') {
          return (
            <FullModeCell
              value={value}
              language={language}
              maxLength={INPUT_TRUNCATE_LIMIT}
            />
          );
        }

        return <CompactModeCell value={value} />;
      },
    }
  ),
  columnHelper.accessor(
    (row) => {
      return row.outputs;
    },
    {
      id: 'outputs',
      header: 'Reference Outputs',
      enableResizing: true,
      enableSorting: false,
      cell: ({ getValue, table, row }) => {
        const { displayMode = 'compact', language = 'json' } = (table.options
          .meta ?? {}) as {
          displayMode?: 'compact' | 'full';
          language?: CodeLanguageType;
        };

        const rawValue = getValue();
        const value =
          displayMode === 'full'
            ? rawValue
            : (
                filterInputs(rawValue, {
                  removeKeys: Object.keys(row.original.inputs).concat([
                    'input',
                    'inputs',
                  ]),
                }) || filterInputs(rawValue)
              )?.length
            ? filterInputs(rawValue, {
                removeKeys: Object.keys(row.original.inputs).concat([
                  'input',
                  'inputs',
                ]),
              }) || filterInputs(rawValue)
            : null;

        if (!value) return <i className="text-tertiary">No outputs</i>;

        if (displayMode === 'full') {
          return (
            <FullModeCell
              value={value}
              language={language}
              maxLength={OUTPUT_TRUNCATE_LIMIT}
            />
          );
        }

        return <CompactModeCell value={value} />;
      },
    }
  ),
  columnHelper.display({
    id: 'attachment_urls',
    header: 'Attachments',
    cell: ({ row }) => {
      const attachments = getAttachments(row.original.attachment_urls);
      return (
        <Attachments
          attachments={attachments}
          className="p-0"
          gridClassName={cn(
            'flex flex-nowrap items-center gap-2 overflow-auto p-0 no-scrollbar'
          )}
          hideTitle
          size="small"
        />
      );
    },
  }),
  columnHelper.accessor('created_at', {
    id: 'recently_created',
    header: 'Created At',
    cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
    enableResizing: true,
    enableSorting: true,
    size: 150,
  }),
  columnHelper.accessor('modified_at', {
    id: 'recent',
    header: 'Modified At',
    cell: ({ row }) => new Date(row.original.modified_at).toLocaleString(),
    enableResizing: true,
    enableSorting: true,
    size: 150,
  }),
];
